import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, Lock } from "lucide-react";
import { Logo } from "../components/brand/Logo";
import { useAuth } from "../components/admin/AuthContext";
import { api, formatApiError } from "../lib/api";

export default function AdminLogin() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = mfaToken
        ? await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code })
        : await api.post("/auth/login", { email, password });
      if (data?.mfa_required) { setMfaToken(data.mfa_token); setBusy(false); return; }
      setUser(data);
      nav("/admin");
    } catch (err) {
      if (mfaToken && err?.response?.status === 401 && /expired/i.test(formatApiError(err))) { setMfaToken(""); setCode(""); }
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tc-grid-bg grid min-h-screen place-items-center bg-[#0C0A09] px-5 text-stone-100">
      <Helmet><title>Admin sign-in | TechCare</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <form onSubmit={submit} className="w-full max-w-sm rounded-[24px] border border-white/10 bg-[#141110]/90 p-8 backdrop-blur-xl" data-testid="admin-login-form">
        <Logo className="h-20 w-auto" />
        <p className="mt-8 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-orange-400"><Lock className="h-3.5 w-3.5" /> Authorised staff only</p>
        <h1 className="mt-3 font-display text-2xl">Enquiry inbox</h1>
        {mfaToken ? (
          <>
            <label className="mt-8 block text-xs text-stone-400" htmlFor="a-code">Two-step verification code</label>
            <input id="a-code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required maxLength={20} autoFocus
              className="mt-2 h-12 w-full rounded-xl border border-white/10 bg-black/40 px-4 font-mono tracking-[0.3em] outline-none focus:border-orange-500" data-testid="admin-mfa-code-input" />
            <p className="mt-2 text-xs text-stone-400">Enter the 6-digit code from your authenticator app, or one of your recovery codes.</p>
          </>
        ) : (<>
        <label className="mt-8 block text-xs text-stone-400" htmlFor="a-email">Email</label>
        <input id="a-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required
          className="mt-2 h-12 w-full rounded-xl border border-white/10 bg-black/40 px-4 outline-none focus:border-orange-500" data-testid="admin-email-input" />
        <label className="mt-5 block text-xs text-stone-400" htmlFor="a-pass">Password</label>
        <input id="a-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required
          className="mt-2 h-12 w-full rounded-xl border border-white/10 bg-black/40 px-4 outline-none focus:border-orange-500" data-testid="admin-password-input" />
        </>)}
        {error && <p className="mt-4 text-sm text-red-400" role="alert" data-testid="admin-login-error">{error}</p>}
        <button disabled={busy} className="tc-btn-primary mt-8 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full text-sm font-semibold" data-testid="admin-login-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {mfaToken ? "Verify" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
